import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  CalendarMonth,
  List,
  Add,
  ExpandMore,
  ExpandLess,
  Person,
  CheckCircle,
  Cancel,
  Schedule,
  Event,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout/Layout';
import LeaveCalendar from '../components/Leave/LeaveCalendar';
import { Leave, LeaveStatus, LeaveType } from '../types';
import { format } from 'date-fns';
import { apiService } from '../services/api';

const LeaveCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [allLeaves, setAllLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      loadAllLeaves();
    }
  };

  const loadAllLeaves = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllLeaves();
      setAllLeaves(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load all leaves:', error);
      setError('Failed to load leave data');
      setAllLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveClick = (leave: Leave) => {
    // Navigate to leave details or approval page based on user role
    if (user?.role === 'admin' || user?.role === 'manager' || 
        user?.role === 'super_manager' || user?.role === 'hr_admin' || 
        user?.role === 'root_user') {
      navigate(`/leaves/approvals`);
    } else {
      navigate(`/leaves/${leave.id}`);
    }
  };

  const handleCreateLeave = () => {
    navigate('/leaves/new');
  };

  const canViewAllLeaves = user?.role === 'admin' || user?.role === 'manager' || 
                          user?.role === 'super_manager' || user?.role === 'hr_admin' || 
                          user?.role === 'root_user';

  const getLeaveTypeColor = (type: LeaveType) => {
    switch (type) {
      case LeaveType.FULL_DAY:
        return 'primary';
      case LeaveType.HALF_DAY:
        return 'secondary';
      case LeaveType.HOURS:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.APPROVED:
        return 'success';
      case LeaveStatus.REJECTED:
        return 'error';
      case LeaveStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getLeaveTypeLabel = (type: LeaveType) => {
    switch (type) {
      case LeaveType.FULL_DAY:
        return 'Full Day';
      case LeaveType.HALF_DAY:
        return 'Half Day';
      case LeaveType.HOURS:
        return 'Hours';
      default:
        return type;
    }
  };

  const getStatusIcon = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.APPROVED:
        return <CheckCircle fontSize="small" />;
      case LeaveStatus.REJECTED:
        return <Cancel fontSize="small" />;
      case LeaveStatus.PENDING:
        return <Schedule fontSize="small" />;
      default:
        return <Event fontSize="small" />;
    }
  };

  const toggleUserExpansion = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const groupLeavesByUser = () => {
    const grouped: { [key: string]: { user: any; leaves: Leave[] } } = {};
    
    allLeaves.forEach(leave => {
      const userId = leave.userId;
      if (!grouped[userId]) {
        grouped[userId] = {
          user: leave.user,
          leaves: []
        };
      }
      grouped[userId].leaves.push(leave);
    });

    return Object.values(grouped).sort((a, b) => 
      `${a.user?.firstName} ${a.user?.lastName}`.localeCompare(`${b.user?.firstName} ${b.user?.lastName}`)
    );
  };

  const renderAllLeavesList = () => {
    const groupedLeaves = groupLeavesByUser();

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      );
    }

    if (groupedLeaves.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No leave requests found
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {groupedLeaves.map(({ user, leaves }) => {
          const isExpanded = expandedUsers.has(user.id);
          const totalLeaves = leaves.length;
          const approvedLeaves = leaves.filter(l => l.status === LeaveStatus.APPROVED).length;
          const pendingLeaves = leaves.filter(l => l.status === LeaveStatus.PENDING).length;
          const rejectedLeaves = leaves.filter(l => l.status === LeaveStatus.REJECTED).length;

          return (
            <Card key={user.id} sx={{ mb: 2 }}>
              <CardContent sx={{ p: 0 }}>
                {/* User Header */}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => toggleUserExpansion(user.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Leave Summary */}
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary">
                        Total: {totalLeaves}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={`${approvedLeaves} Approved`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          label={`${pendingLeaves} Pending`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                        <Chip
                          label={`${rejectedLeaves} Rejected`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    
                    <IconButton size="small">
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Leave Details */}
                <Collapse in={isExpanded}>
                  <Divider />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Date Range</TableCell>
                          <TableCell>Hours</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Approved By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leaves
                          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                          .map((leave) => (
                          <TableRow 
                            key={leave.id}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                            onClick={() => handleLeaveClick(leave)}
                          >
                            <TableCell>
                              <Chip
                                label={getLeaveTypeLabel(leave.type)}
                                size="small"
                                color={getLeaveTypeColor(leave.type) as any}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {format(new Date(leave.startDate), 'MMM dd, yyyy')} - {format(new Date(leave.endDate), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              {leave.type === LeaveType.HOURS ? `${leave.hours} hours` : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {leave.reason}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={leave.status}
                                size="small"
                                color={getStatusColor(leave.status) as any}
                                icon={getStatusIcon(leave.status)}
                              />
                            </TableCell>
                            <TableCell>
                              {format(new Date(leave.createdAt), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              {leave.approver ? (
                                <Typography variant="body2">
                                  {leave.approver.firstName} {leave.approver.lastName}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Leave Calendar
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateLeave}
          >
            Apply Leave
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab 
              icon={<CalendarMonth />} 
              label="Calendar View" 
              iconPosition="start"
            />
            {canViewAllLeaves && (
              <Tab 
                icon={<List />} 
                label="All Leaves" 
                iconPosition="start"
              />
            )}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {canViewAllLeaves ? 'All Employee Leaves' : 'My Leaves'}
              </Typography>
              <LeaveCalendar
                showAllEmployees={canViewAllLeaves}
                onLeaveClick={handleLeaveClick}
              />
            </Box>
          )}
          
          {activeTab === 1 && canViewAllLeaves && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                All Employee Leaves - List View
              </Typography>
              {renderAllLeavesList()}
            </Box>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default LeaveCalendarPage;
